import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import API_URL from '../../config';
import ReservationCard from '../common/ReservationCard';
import './ManagerPanel.css';

function ManagerPanel() {
  const [requests, setRequests] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filter and sort state
  const [sortBy, setSortBy] = useState('reservation_number');
  const [sortDirection, setSortDirection] = useState('desc');
  const [filterYear, setFilterYear] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterFamily, setFilterFamily] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const { getAuthHeader } = useAuth();

  const fetchRequests = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/reservations/requests`, {
        headers: getAuthHeader()
      });
      const data = await response.json();
      // Ensure data is an array (API may return error object)
      setRequests(Array.isArray(data) ? data : []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching requests:', error);
      setLoading(false);
    }
  }, [getAuthHeader]);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/users`, {
        headers: getAuthHeader()
      });
      const data = await response.json();
      // Ensure data is an array (API may return error object)
      setUsers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchRequests();
    fetchUsers();
    const interval = setInterval(fetchRequests, 5000);
    return () => clearInterval(interval);
  }, [fetchRequests, fetchUsers]);

  // eslint-disable-next-line no-unused-vars
  const handleCancel = async (id) => {
    const reason = prompt('Enter cancellation reason (optional):');
    if (reason === null) return;

    try {
      const response = await fetch(`${API_URL}/api/reservations/cancel/${id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        },
        body: JSON.stringify({ reason })
      });
      const data = await response.json();
      if (data.success) {
        setRequests(requests.map(r => r.id === id ? { ...r, status: 'cancelled' } : r));
        alert('Reservation cancelled!');
      }
    } catch (error) {
      console.error('Error cancelling request:', error);
    }
  };

  // Payment handlers (managers can add and update, but not delete)
  const handleAddPayment = async (reservationId, paymentData) => {
    try {
      const response = await fetch(`${API_URL}/api/payments/reservation/${reservationId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        },
        body: JSON.stringify(paymentData)
      });
      const data = await response.json();
      if (data.success) {
        fetchRequests();
        return { success: true, payment: data.payment };
      } else {
        alert(`Error: ${data.error || 'Failed to add payment'}`);
        return { success: false };
      }
    } catch (error) {
      console.error('Error adding payment:', error);
      alert('Failed to add payment');
      return { success: false };
    }
  };

  const handleUpdatePayment = async (paymentId, paymentData) => {
    try {
      const response = await fetch(`${API_URL}/api/payments/${paymentId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        },
        body: JSON.stringify(paymentData)
      });
      const data = await response.json();
      if (data.success) {
        fetchRequests();
        return { success: true, payment: data.payment };
      } else {
        alert(`Error: ${data.error || 'Failed to update payment'}`);
        return { success: false };
      }
    } catch (error) {
      console.error('Error updating payment:', error);
      alert('Failed to update payment');
      return { success: false };
    }
  };

  const handleVerifyPayment = async (paymentId) => {
    try {
      const response = await fetch(`${API_URL}/api/payments/${paymentId}/verify`, {
        method: 'POST',
        headers: getAuthHeader()
      });
      const data = await response.json();
      if (data.success) {
        fetchRequests();
        return { success: true };
      } else {
        alert(`Error: ${data.error || 'Failed to verify payment'}`);
        return { success: false };
      }
    } catch (error) {
      console.error('Error verifying payment:', error);
      alert('Failed to verify payment');
      return { success: false };
    }
  };

  const handleRejectPayment = async (paymentId, reason) => {
    try {
      const response = await fetch(`${API_URL}/api/payments/${paymentId}/reject`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        },
        body: JSON.stringify({ reason })
      });
      const data = await response.json();
      if (data.success) {
        fetchRequests();
        return { success: true };
      } else {
        alert(`Error: ${data.error || 'Failed to reject payment'}`);
        return { success: false };
      }
    } catch (error) {
      console.error('Error rejecting payment:', error);
      alert('Failed to reject payment');
      return { success: false };
    }
  };

  const pendingRequests = requests.filter(r => r.status === 'pending');

  const approvedRequests = requests
    .filter(r => r.status === 'approved')
    .filter(r => {
      if (filterYear !== 'all' && String(r.year) !== filterYear) return false;
      if (filterType !== 'all' && r.type !== filterType) return false;
      if (filterFamily !== 'all') {
        const emailDomain = r.email?.toLowerCase() || '';
        const description = r.description?.toLowerCase() || '';
        const familyLower = filterFamily.toLowerCase();
        if (!emailDomain.includes(familyLower) && !description.includes(familyLower)) return false;
      }
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const searchableText = [
          r.reservation_number || '',
          r.description || '',
          r.notes || '',
          new Date(r.check_in_date).toLocaleDateString(),
          new Date(r.check_out_date).toLocaleDateString(),
          r.email || ''
        ].join(' ').toLowerCase();
        if (!searchableText.includes(query)) return false;
      }
      return true;
    })
    .sort((a, b) => {
      let compareValue = 0;
      switch(sortBy) {
        case 'reservation_number':
          compareValue = (a.reservation_number || '').localeCompare(b.reservation_number || '');
          break;
        case 'check_in_date':
          compareValue = new Date(a.check_in_date) - new Date(b.check_in_date);
          break;
        case 'year':
          compareValue = (a.year || 0) - (b.year || 0);
          break;
        case 'type':
          compareValue = (a.type || '').localeCompare(b.type || '');
          break;
        default:
          compareValue = 0;
      }
      return sortDirection === 'asc' ? compareValue : -compareValue;
    });

  const rejectedRequests = requests.filter(r => r.status === 'rejected');
  const cancelledRequests = requests.filter(r => r.status === 'cancelled');

  if (loading) return <div>Loading...</div>;

  return (
    <div className="manager-panel">
      <h2>Manager Dashboard</h2>

      <div className="manager-stats">
        <div className="stat-card">
          <div className="stat-number">{pendingRequests.length}</div>
          <div className="stat-label">Pending</div>
        </div>
        <div className="stat-card approved">
          <div className="stat-number">{approvedRequests.length}</div>
          <div className="stat-label">Approved</div>
        </div>
        <div className="stat-card rejected">
          <div className="stat-number">{rejectedRequests.length}</div>
          <div className="stat-label">Rejected</div>
        </div>
        <div className="stat-card cancelled">
          <div className="stat-number">{cancelledRequests.length}</div>
          <div className="stat-label">Cancelled</div>
        </div>
      </div>

      {/* Pending Requests */}
      <div className="requests-section">
        <h3>Pending Requests</h3>
        {pendingRequests.length === 0 ? (
          <p className="no-requests">No pending requests</p>
        ) : (
          <div className="requests-list">
            {pendingRequests.map(req => (
              <div key={req.id} className="request-card pending">
                <div className="request-info">
                  <p><strong>Email:</strong> {req.email}</p>
                  <p><strong>Check-in:</strong> {new Date(req.check_in_date).toLocaleString()}</p>
                  <p><strong>Check-out:</strong> {new Date(req.check_out_date).toLocaleString()}</p>
                  {req.description && <p><strong>Description:</strong> {req.description}</p>}
                  {req.notes && <p><strong>Notes:</strong> {req.notes}</p>}
                  {req.notify_cleaning && <p><strong>Cleaning:</strong> Requested</p>}
                  <p><strong>Requested:</strong> {new Date(req.created_at).toLocaleDateString()}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Approved Reservations */}
      <div className="requests-section">
        <h3>Approved Reservations</h3>

        <div className="filter-controls">
          <div className="filter-row">
            <div className="sort-controls">
              <label className="filter-label">Sort by:</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="filter-select"
              >
                <option value="reservation_number">Reservation Number</option>
                <option value="check_in_date">Check-in Date</option>
                <option value="year">Year</option>
                <option value="type">Type</option>
              </select>
              <button
                onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
                className="btn btn-primary"
              >
                {sortDirection === 'asc' ? 'Ascending' : 'Descending'}
              </button>
            </div>
          </div>

          <div className="filter-row">
            <label className="filter-label">Filter:</label>
            <select
              value={filterYear}
              onChange={(e) => setFilterYear(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Years</option>
              {[...new Set(requests.filter(r => r.status === 'approved').map(r => r.year).filter(Boolean))]
                .sort((a, b) => b - a)
                .map(year => <option key={year} value={year}>{year}</option>)}
            </select>

            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Types</option>
              {[...new Set(requests.filter(r => r.status === 'approved').map(r => r.type).filter(Boolean))]
                .sort()
                .map(type => <option key={type} value={type}>{type}</option>)}
            </select>

            <select
              value={filterFamily}
              onChange={(e) => setFilterFamily(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Families</option>
              {[...new Set(users.map(u => u.family).filter(Boolean))]
                .sort()
                .map(family => <option key={family} value={family}>{family}</option>)}
            </select>

            <input
              type="text"
              placeholder="Search reservations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
          </div>

          {(filterYear !== 'all' || filterType !== 'all' || filterFamily !== 'all' || searchQuery) && (
            <div className="filter-summary">
              <span>Showing {approvedRequests.length} of {requests.filter(r => r.status === 'approved').length} reservations</span>
              <button
                onClick={() => {
                  setFilterYear('all');
                  setFilterType('all');
                  setFilterFamily('all');
                  setSearchQuery('');
                }}
                className="btn btn-neutral"
              >
                Clear Filters
              </button>
            </div>
          )}
        </div>

        {approvedRequests.length === 0 ? (
          <p className="no-requests">No approved reservations</p>
        ) : (
          <div className="requests-list">
            {approvedRequests.map(req => (
              <ReservationCard
                key={req.id}
                reservation={{ ...req, user_name: req.user_name || users.find(u => u.email === req.email)?.name }}
                canEditPayments={true}
                canDeletePayments={false}
                onAddPayment={handleAddPayment}
                onUpdatePayment={handleUpdatePayment}
                onVerifyPayment={handleVerifyPayment}
                onRejectPayment={handleRejectPayment}
              />
            ))}
          </div>
        )}
      </div>

      {/* Rejected Requests */}
      <div className="requests-section">
        <h3>Rejected Requests</h3>
        {rejectedRequests.length === 0 ? (
          <p className="no-requests">No rejected requests</p>
        ) : (
          <div className="requests-list">
            {rejectedRequests.map(req => (
              <div key={req.id} className="request-card rejected">
                <div className="request-info">
                  <p><strong>Email:</strong> {req.email}</p>
                  <p><strong>Check-in:</strong> {new Date(req.check_in_date).toLocaleString()}</p>
                  <p><strong>Check-out:</strong> {new Date(req.check_out_date).toLocaleString()}</p>
                  {req.description && <p><strong>Description:</strong> {req.description}</p>}
                  <p><strong>Rejected:</strong> {req.rejected_at ? new Date(req.rejected_at).toLocaleDateString() : 'N/A'}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Cancelled Reservations */}
      <div className="requests-section">
        <h3>Cancelled Reservations</h3>
        {cancelledRequests.length === 0 ? (
          <p className="no-requests">No cancelled reservations</p>
        ) : (
          <div className="requests-list">
            {cancelledRequests.map(req => (
              <div key={req.id} className="request-card cancelled">
                <div className="request-info">
                  <p><strong>Email:</strong> {req.email}</p>
                  <p><strong>Check-in:</strong> {new Date(req.check_in_date).toLocaleString()}</p>
                  <p><strong>Check-out:</strong> {new Date(req.check_out_date).toLocaleString()}</p>
                  {req.description && <p><strong>Description:</strong> {req.description}</p>}
                  {req.cancellation_reason && <p><strong>Reason:</strong> {req.cancellation_reason}</p>}
                  <p><strong>Cancelled:</strong> {req.cancelled_at ? new Date(req.cancelled_at).toLocaleDateString() : 'N/A'}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default ManagerPanel;
